import { useMemo } from 'react';

import { useGetLineItemQuery } from '../GraphQL/LineItemDetailsScreen.getLineItem.generated';
import { useGetProductQuery } from '../GraphQL/ProductDetailsScreen.getProduct.generated';

export const useProduct = (props: UseProductProps) => {
  const { productSlug, lineItemId = '' } = props;

  // ─── QUERIES ────────────────────────────────────────────────────────

  const [productResponse] = useGetProductQuery({
    variables: { id: productSlug },
    requestPolicy: 'cache-and-network',
  });

  const [lineItemResponse] = useGetLineItemQuery({
    variables: { input: { id: lineItemId } },
    requestPolicy: 'cache-and-network',
    pause: !lineItemId,
  });

  // ─── DATA ───────────────────────────────────────────────────────────

  const product = useMemo(
    () => getProductCtx(productResponse),
    [productResponse],
  );
  const lineItem = useMemo(
    () => getLineItemCtx(lineItemResponse),
    [lineItemResponse],
  );
  const ingredients = useMemo(
    () => getIngredientsCtx({ product, lineItem }),
    [lineItem, product],
  );

  const stale = product.stale || lineItem.stale;
  const fetching = product.fetching || lineItem.fetching;
  const errorMessage = product.errorMessage ?? lineItem.errorMessage ?? '';
  const error = useMemo(
    () => (errorMessage ? { message: errorMessage } : null),
    [errorMessage],
  );

  // ────────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      lineItem: lineItem.data,
      product: product.data,
      ingredients,
      fetching,
      stale,
      error,
    }),
    [error, fetching, ingredients, lineItem.data, product.data, stale],
  );
};

// ─── UTILS ──────────────────────────────────────────────────────────────────────

export const getProductCtx = (queryResponse: ProductQueryResponse) => {
  const data = queryResponse.data?.product ?? null;
  const errorMessage = queryResponse.error?.message ?? '';
  const { fetching = false, stale = false } = queryResponse;

  return { data, errorMessage, fetching, stale };
};

export const getLineItemCtx = (queryResponse: LineItemQueryResponse) => {
  const lineItemData = queryResponse.data?.getLineItem;

  const isLineItemResolved = lineItemData?.__typename === 'LineItemSuccess';
  const isLineItemNotFound = lineItemData?.__typename === 'LineItemNotFound';
  const isLineItemNotValid = lineItemData?.__typename === 'ValidationError';

  const responseErrorMessage = queryResponse.error?.message;
  const lineItemErrorMessage =
    isLineItemNotFound || isLineItemNotValid ? lineItemData.message : undefined;

  const data = isLineItemResolved ? lineItemData.lineItem : null;
  const errorMessage = responseErrorMessage ?? lineItemErrorMessage ?? '';
  const { fetching = false, stale = false } = queryResponse;

  return { data, errorMessage, fetching, stale };
};

export const getIngredientsCtx = (props: GetIngredientsCtxProps) => {
  const { product, lineItem } = props;

  const areAllQueriesResolved = !product.fetching && !lineItem.fetching;

  const productIngredients = product.data?.ingredients ?? null;
  const maxModifications = Number(product.data?.maxModifications);
  const lineItemIngredients = lineItem.data?.ingredients ?? null;

  const ingredientsModifications =
    product.data?.ingredientModifications ?? null;
  const defaultIngredients = productIngredients;
  const activeIngredients = lineItemIngredients ?? productIngredients;
  const mixedDressingDetails = lineItem?.data?.mixedDressingDetails ?? [];

  // ────────────────────────────────────────────────────────────────────

  const isDataResolved =
    areAllQueriesResolved &&
    ingredientsModifications &&
    defaultIngredients &&
    activeIngredients;

  if (isDataResolved)
    return {
      ingredientsModifications,
      defaultIngredients,
      activeIngredients,
      maxModifications,
      mixedDressingDetails,
    };

  return null;
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type UseProductProps = {
  readonly productSlug: string;
  readonly lineItemId: string | undefined;
};

export type GetIngredientsCtxProps = {
  readonly product: ReturnType<typeof getProductCtx>;
  readonly lineItem: ReturnType<typeof getLineItemCtx>;
};

export type ProductQueryResponse = ReturnType<typeof useGetProductQuery>[0];

export type LineItemQueryResponse = ReturnType<typeof useGetLineItemQuery>[0];
